'use client';

import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Camera, Loader2, X, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { postMultipartImageUpload } from '@/lib/vertex-upload';
import { ImageCropper } from '@/components/ui/image-cropper';
import { ImageFileInput } from '@/components/forms/image-file-input';

interface ImageUploadProps {
  value?: string;
  onChange: (value: string) => void | Promise<void>;
  disabled?: boolean;
}

export function ImageUpload({ value, onChange, disabled }: ImageUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [converting, setConverting] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handlePreparedDataUrl = (dataUrl: string) => {
    setSelectedImage(dataUrl);
  };

  const onCropComplete = async (croppedBlob: Blob) => {
    setIsUploading(true);

    const formData = new FormData();
    formData.append('file', croppedBlob, 'cropped-image.webp');
    formData.append('folder', 'profile-photos');

    try {
      const { url } = await postMultipartImageUpload(formData);
      await Promise.resolve(onChange(url));
      toast.success('Profile photo saved');
      setSelectedImage(null);
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Failed to save profile photo');
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (disabled || isUploading) return;
    setIsUploading(true);
    try {
      await Promise.resolve(onChange(''));
      toast.success('Profile photo removed');
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Failed to remove profile photo');
    } finally {
      setIsUploading(false);
    }
  };

  const isBusy = disabled || isUploading || converting;

  return (
    <>
      <div className="flex flex-col items-center gap-4 w-full">
        <div className="relative group">
          <Avatar
            className="h-32 w-32 border-4 border-white bg-white ring-2 ring-primary/10 shadow-xl cursor-pointer transition-transform hover:scale-105 active:scale-95 dark:border-black dark:bg-black"
            onClick={() => !isBusy && fileInputRef.current?.click()}
          >
            <AvatarImage src={value} className="object-cover" />
            <AvatarFallback className="bg-white text-muted-foreground dark:bg-black">
              {isUploading || converting ? (
                <Loader2 className="h-10 w-10 animate-spin" />
              ) : (
                <Camera className="h-10 w-10" />
              )}
            </AvatarFallback>
          </Avatar>

          {(isUploading || converting) && (
            <div className="absolute inset-0 flex items-center justify-center rounded-full bg-background/80 backdrop-blur-sm">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}

          {value && !isBusy && (
            <button
              onClick={handleRemove}
              className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground rounded-full p-1.5 shadow-lg hover:scale-110 transition-all opacity-0 group-hover:opacity-100"
              type="button"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="flex flex-col items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isBusy}
            onClick={() => fileInputRef.current?.click()}
            className="rounded-full px-6 border-primary/20 hover:border-primary/50"
          >
            {isUploading ? (
              <>
                <Loader2 className="mr-2 size-4 animate-spin" />
                Saving…
              </>
            ) : converting ? (
              <>
                <Loader2 className="mr-2 size-4 animate-spin" />
                Converting…
              </>
            ) : (
              <>
                <Upload className="mr-2 size-4" />
                {value ? 'Change Photo' : 'Upload Photo'}
              </>
            )}
          </Button>
          <ImageFileInput
            ref={fileInputRef}
            className="hidden"
            accept="image/*"
            onPreparedDataUrl={handlePreparedDataUrl}
            onPreparingChange={setConverting}
            disabled={isBusy}
          />
          <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-semibold">
            Recommended: square webp/jpg, max 5MB
          </p>
        </div>
      </div>

      {selectedImage && (
        <ImageCropper
          imageSrc={selectedImage}
          onCropComplete={onCropComplete}
          onCancel={() => !isUploading && setSelectedImage(null)}
          aspect={1}
        />
      )}
    </>
  );
}
